import { useLocalObservable } from 'mobx-react-lite';
import { ElementType, FC, useEffect } from 'react';

import { IcEditIcon, IcUsersIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import type { PopupStore } from '@gem/popups';
import { ICommonPopupsProps } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { EditWorkspacePopup } from 'APP/main/PopupManager/Popups/EditWorkspacePopup/EditWorkspacePopup';
import { WorkspaceMembersPopup } from 'APP/main/PopupManager/Popups/WorkspaceMembersPopup/WorkspaceMembersPopup';
import { WorkspaceRole } from 'APP/model/workspace/workspaceModel.types';
import Entities from 'STORE';
import type { Workspace } from 'STORE/Workspace/Workspace';

interface IMenuItem {
  key: string;
  title: string;
  count?: number;
  Icon: ElementType;
  onClick(): void;
}

interface IWorkspaceProfilePresenter {
  childPopup: PopupStore | null;
  workspace: Workspace;
  menuItems: IMenuItem[];
  canEditProfile: boolean;
  openMembers(): void;
  openEditProfile(): void;
  openChildPopup(component: FC<ICommonPopupsProps>): void;
}

export function useWorkspaceProfilePresenter(
  popupInstance?: PopupStore
): IWorkspaceProfilePresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IWorkspaceProfilePresenter>(() => ({
    childPopup: null,

    get workspace(): Workspace {
      return Entities.workspace;
    },

    get menuItems(): IMenuItem[] {
      return [
        {
          key: 'members',
          Icon: IcUsersIcon,
          title: t('org_members'),
          isVisible: true,
          count: presenter.workspace.membersCount,
          onClick: () => presenter.openMembers(),
        },
        {
          key: 'edit',
          Icon: IcEditIcon,
          title: t('common_edit'),
          isVisible: false, //this.canEditProfile,
          count: undefined,
          onClick: () => presenter.openEditProfile(),
        },
      ].filter((item) => item.isVisible);
    },

    openMembers(): void {
      presenter.openChildPopup(WorkspaceMembersPopup);
    },

    get canEditProfile(): boolean {
      return [WorkspaceRole.OWNER, WorkspaceRole.ADMIN].includes(this.workspace.role);
    },

    openEditProfile(): void {
      presenter.openChildPopup(EditWorkspacePopup);
    },

    openChildPopup(component: FC<ICommonPopupsProps>): void {
      popupInstance?.hide();

      this.childPopup = Tasks.app.openPopup(component, {
        onClose: () => popupInstance?.close(),
        onBack: () => popupInstance?.show(),
      });
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
}
